import { useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";

import { useSetup } from "./api/hooks";
import { isChatContextError } from "./api/errors";
import { setSessionChatId, verifySession } from "./auth/session";
import { ChatGate } from "./components/ChatGate";
import { NoChatContext } from "./components/NoChatContext";
import { ErrorNotice, Loading } from "./components/primitives";
import { SetupPicker } from "./components/SetupPicker";
import { TabBar } from "./components/TabBar";
import { showBackButtonWith } from "./telegram/sdk";
import { queryKeys } from "./api/hooks";
import { GeoMap } from "./screens/GeoMap";
import { Home } from "./screens/Home";
import { Leaderboard } from "./screens/Leaderboard";
import { Profile } from "./screens/Profile";
import { Quest } from "./screens/Quest";
import { RiddleModal } from "./screens/RiddleModal";

type BootState =
  | { phase: "loading" }
  | { phase: "pick" } // launched from DM (authed, no chat) → NoChatContext picker
  | { phase: "gate" } // no token / hard chat-context error → ChatGate
  | { phase: "error"; message: string }
  | { phase: "ready" };

const Shell = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Content = styled.main`
  flex: 1;
  padding: ${({ theme }) => theme.space(4)};
  padding-bottom: ${({ theme }) => theme.space(6)};
`;

export function App() {
  const [boot, setBoot] = useState<BootState>({ phase: "loading" });
  // True when launched from a DM (no chat context): drives the chat picker and
  // the "back to picker" escape hatch once a chat is chosen.
  const [fromDm, setFromDm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { chatId } = await verifySession();
        if (cancelled) return;
        if (chatId === undefined) {
          // Authenticated but no group context → let the user choose a chat.
          setFromDm(true);
          setBoot({ phase: "pick" });
        } else {
          setBoot({ phase: "ready" });
        }
      } catch (e) {
        if (cancelled) return;
        if (isChatContextError(e)) {
          // No token (e.g. no initData at all) → the picker can't authenticate.
          setBoot({ phase: "gate" });
        } else {
          setBoot({ phase: "error", message: e instanceof Error ? e.message : "Failed to start" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // After a DM user picks a chat, wire the Telegram BackButton to return to the
  // picker so they aren't trapped in the chosen chat for the rest of the session.
  useEffect(() => {
    if (!fromDm || boot.phase !== "ready") return;
    return showBackButtonWith(() => setBoot({ phase: "pick" }));
  }, [fromDm, boot.phase]);

  if (boot.phase === "loading") return <Loading label="waking rocky…" />;
  if (boot.phase === "pick") {
    return (
      <NoChatContext
        onSelectChat={(chatId) => {
          setSessionChatId(chatId);
          setBoot({ phase: "ready" });
        }}
      />
    );
  }
  if (boot.phase === "gate") return <ChatGate />;
  if (boot.phase === "error") return <ErrorNotice message={boot.message} />;

  return <GatedApp />;
}

/**
 * Runs after auth succeeds with a chatId. Probes setup via GET .../setup: if the
 * chat hasn't completed setup it renders the language picker; chat-context
 * errors fall through to the ChatGate. Once setup is complete, the routed app
 * renders.
 */
function GatedApp() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useSetup();

  if (isLoading) return <Loading label="loading…" />;
  if (isChatContextError(error)) return <ChatGate />;
  if (error) return <ErrorNotice message={error.message} />;

  if (data && !data.setupComplete) {
    return (
      <SetupPicker
        onDone={() => {
          void qc.invalidateQueries({ queryKey: queryKeys.setup() });
          void refetch();
        }}
      />
    );
  }

  return <RoutedApp />;
}

function RoutedApp() {
  const location = useLocation();
  const navigate = useNavigate();

  // Riddle is an overlay route over Home: keep Home mounted underneath and use
  // a background-location so dismissing returns to the home tab.
  const state = location.state as { backgroundLocation?: Location } | null;
  const backgroundLocation = state?.backgroundLocation;

  return (
    <Shell>
      <Content>
        <Routes location={backgroundLocation ?? location}>
          <Route path="/" element={<Home />} />
          <Route path="/quest" element={<Quest />} />
          <Route path="/board" element={<Leaderboard />} />
          {/* Sub-route of /board so the board tab stays highlighted (NavLink prefix match). */}
          <Route path="/board/map" element={<GeoMap />} />
          <Route path="/me" element={<Profile />} />
          <Route path="*" element={<Home />} />
        </Routes>

        {/* Overlay route */}
        <Routes>
          <Route
            path="/riddle"
            element={<RiddleModal onClose={() => navigate("/", { replace: true })} />}
          />
        </Routes>
      </Content>
      <TabBar />
    </Shell>
  );
}
